/* eslint-disable @typescript-eslint/no-unused-vars */
import {
  ComponentProps,
  FC,
  MouseEvent,
  ReactNode,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { VariantProps, cva } from 'class-variance-authority';
import { debounce, isArray, isEmpty } from 'lodash';
import { BsInfoCircle } from 'react-icons/bs';
import { FaPlus } from 'react-icons/fa';
import { HiOutlineMagnifyingGlass } from 'react-icons/hi2';
import { IoMdPlay } from 'react-icons/io';
import { VscClose } from 'react-icons/vsc';
import {
  ControlProps,
  GroupBase,
  OptionProps,
  StylesConfig,
  components,
} from 'react-select';
import AsyncSelect, { AsyncProps } from 'react-select/async';
import type {} from 'react-select/base';
import Button from 'ui/components/common/Button';
import Tooltip from 'ui/components/common/Tooltip';
import Avatar, { AvatarGroup } from 'ui/components/img/Avatar';
import { SIZE } from 'utils/constants';
import { cn } from 'utils/helpers';

import Checkbox from '../Checkbox';
import { inputClasses } from '../Input/Input';

export interface AvatarOption {
  photoUrl?: string | null;
  itemLabel: string;
  description?: string;
  value: any;
  voiceUrl?: string;
}

declare module 'react-select/base' {
  export interface Props<Option, IsMulti, Group extends GroupBase<Option>> {
    isCreatable?: boolean;
    onClickCreate?: () => void;
  }
}

type PROPS<
  Option = unknown,
  IsMulti extends boolean = false | true,
  Group extends GroupBase<Option> = GroupBase<Option>,
> = AsyncProps<Option, IsMulti, Group> &
  VariantProps<typeof avatarSelectControl> & {
    label?: string;
    labelSubText?: string;
    information?: string;
    required?: boolean;
    subText?: string;
    onClear?: () => void;
    noOptionPlaceholder?: string;
    hideCheckbox?: boolean;
    onClickAvatar?: (opt: OptionProps<AvatarOption>) => void;
  };

const avatarSelectControl = cva(
  [
    'pr-2 text-content',
    '!shadow-none rounded-md',
    'text-sm leading-5 tracking-[0.56px]',
    'border border-separator-secondary',
    'disabled:border-fill-secondary disabled:text-disabled',
    'group-focus:border-primary-base group-focus-visible:border-primary-base',
  ],
  {
    variants: {
      size: {
        sm: ['text-sm'],
        md: ['text-sm', 'h-[42px]'],
        lg: ['text-base', 'h-[52px]', 'px-2'],
      },
      isFocused: {
        true: ['!border-primary-base'],
      },
      state: {
        danger: ['!border-danger-base'],
        warning: ['!border-warning-base'],
        success: ['!border-success-base'],
      },
    },
    defaultVariants: {
      size: 'sm',
    },
  },
);

const subtext = cva(['leading-4 text-content mt-2'], {
  variants: {
    state: {
      default: [],
      danger: ['!text-danger-base'],
      warning: ['!text-warning-base'],
      success: ['!text-success-base'],
    },
    size: {
      sm: 'text-xs',
      md: 'text-xs',
      lg: 'text-sm',
    },
  },
});

const Menu = (props: JSX.IntrinsicElements['div']) => {
  return (
    <div
      className={cn(
        'bg-fill-tertiary rounded-md mt-2',
        'border border-secondary-lightest',
        'absolute z-10 w-full',
      )}
      {...props}
    />
  );
};

interface CONTROL_PROPS extends ControlProps<AvatarOption> {
  onClose: () => void;
}

const Control: FC<CONTROL_PROPS> = ({ children, onClose, ...props }) => {
  const selected = props.getValue();

  return (
    <div className="space-y-2 border-b border-separator-secondary">
      <div className="flex items-center justify-between">
        {props.selectProps.isCreatable && (
          <Button
            onClick={() => {
              props.selectProps.onClickCreate?.();
              onClose();
            }}
            label="Add New Record"
            left
            icon={FaPlus}
            intent="link"
            disabled={!props.selectProps.isCreatable}
            className="flex items-center gap-2 py-2 px-3 text-sm"
          />
        )}
        <Button
          onClick={() => {
            props.clearValue();
          }}
          intent="link"
          label="Clear Selected"
          className="py-2 px-3"
        />
      </div>
      <components.Control {...props} className="flex items-center px-2">
        <HiOutlineMagnifyingGlass className={cn('h-6 w-6', 'text-content')} />
        {children}
      </components.Control>
      {isArray(selected) && (
        <div className="space-y-1">
          {selected.map((item) => (
            <div className="">
              <div
                tabIndex={0}
                role="button"
                onClick={() => props.selectOption(item)}
                {...props.innerProps}
                className={cn(
                  'flex flex-1 gap-[14px] !items-center h-full',
                  'px-4 py-[10px] hover:bg-fill-primary',
                  'hover:bg-fill-primary focus:bg-fill-primary !text-secondary-dark',
                )}
              >
                <Checkbox defaultChecked className="pointer-events-none" />
                <Avatar
                  size={SIZE.Small}
                  user={{ fullName: item.itemLabel, photoUrl: item.photoUrl }}
                />
                <div className="max-w-[100%] truncate">
                  <p className="text-sm leading-5 truncate text-secondary">
                    {item?.itemLabel}
                  </p>
                  <p className="text-content text-xs leading-4 truncate">
                    {item?.description}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const Blanket = (props: JSX.IntrinsicElements['div']) => (
  <div className="bottom-0 left-0 right-0 top-0 fixed z-[1px]" {...props} />
);

const Option = (
  props: OptionProps<AvatarOption> & {
    hideCheckbox?: boolean;
    onClickAvatar?: (opt: OptionProps<AvatarOption>) => void;
  },
) => {
  const [isHovered, setIsHovered] = useState(false);

  const handleClickAvatar = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    e.preventDefault();
    props.onClickAvatar?.(props);
  };

  const handleHoverEnter = () => {
    if (props.onClickAvatar) setIsHovered(true);
  };

  const handleHoverLeave = () => {
    if (props.onClickAvatar) setIsHovered(false);
  };

  const { data, selectProps, isFocused, isSelected } = props;
  if (!data) {
    return (
      <components.Option {...props}>
        <div
          {...props.innerProps}
          role="button"
          className={cn(
            'hover:bg-fill-primary break-all text-center px-4 py-[10px]',
            isFocused && 'bg-fill-primary',
          )}
        >
          Create {selectProps.inputValue}...
        </div>
      </components.Option>
    );
  }
  return (
    <components.Option
      {...props}
      className={cn(
        '!flex gap-[14px] !items-center',
        '!px-4 !py-[10px] hover:bg-fill-primary',
        '!cursor-pointer',
        isFocused && '!bg-fill-primary !text-secondary-dark',
        isSelected && '!bg-fill-primary !text-secondary-dark',
      )}
    >
      {!props.hideCheckbox && <Checkbox />}
      <div onMouseEnter={handleHoverEnter} onMouseLeave={handleHoverLeave}>
        {isHovered ? (
          <button
            className={cn(
              'cursor-pointer flex items-center justify-center',
              'bg-primary-base rounded-full h-6 w-6',
            )}
            onClick={handleClickAvatar}
          >
            <IoMdPlay className="h-3 w-3 text-fill-tertiary ml-0.5" />
          </button>
        ) : (
          <Avatar
            size={SIZE.Small}
            user={{ fullName: data.itemLabel, photoUrl: data.photoUrl }}
          />
        )}
      </div>
      <div className="max-w-[100%] truncate">
        <p className="text-sm leading-5 truncate text-secondary">{`${data?.itemLabel}`}</p>
        <p className="text-content text-xs leading-4 truncate">
          {data?.description}
        </p>
      </div>
    </components.Option>
  );
};

const Dropdown = ({
  children,
  isOpen,
  target,
  onClose,
}: {
  children?: ReactNode;
  readonly isOpen: boolean;
  readonly target: ReactNode;
  readonly onClose: () => void;
}) => (
  <div className="relative group">
    {target}
    {isOpen && <Blanket onClick={onClose} />}
    {isOpen && <Menu>{children}</Menu>}
  </div>
);

const selectStyles: StylesConfig<AvatarOption, boolean> = {
  control: (provided) => ({
    ...provided,
    // minWidth: 320,
    margin: 8,
  }),
  menu: () => ({ boxShadow: 'inset 0 1px 0 rgba(0, 0, 0, 0.1)' }),
};

const SelectAvatar: FC<PROPS<AvatarOption>> = ({
  label,
  labelSubText,
  information,
  required,
  subText,
  size,
  state,
  name,
  placeholder,
  onChange,
  onClear,
  defaultValue,
  noOptionPlaceholder,
  hideCheckbox,
  onClickAvatar,
  ...props
}) => {
  const noOptionsMessage: ComponentProps<
    typeof AsyncSelect
  >['noOptionsMessage'] = ({ inputValue }) => {
    if (noOptionPlaceholder)
      return (
        <p className="text-center text-sm text-content">
          {noOptionPlaceholder}
        </p>
      );

    let text = `No Results for ${inputValue}`;

    if (!inputValue) {
      text = 'type or search';
    }

    return <p className="text-center text-sm text-content">{text}</p>;
  };

  const [isOpen, setIsOpen] = useState(false);
  const [value, setValue] = useState<AvatarOption | AvatarOption[] | null>(
    defaultValue as AvatarOption | AvatarOption[],
  );

  useEffect(() => {
    if (defaultValue) {
      setValue(defaultValue as AvatarOption | AvatarOption[]);
    }
  }, [defaultValue]);

  const renderValue = useMemo(() => {
    if (isArray(value)) {
      if (!value.length) {
        return null;
      }

      if (value.length === 1) {
        return (
          <div className="flex gap-[14px] items-center h-full">
            <Avatar
              size={SIZE.Small}
              user={{
                fullName: value[0]?.itemLabel,
                photoUrl: value[0]?.photoUrl,
              }}
            />
            {value[0]?.itemLabel}
          </div>
        );
      }
      return (
        <AvatarGroup
          className="ml-1"
          size={SIZE.Small}
          users={value.map(({ itemLabel, photoUrl }) => ({
            fullName: itemLabel,
            photoUrl: photoUrl,
          }))}
        />
      );
    }
    if (value)
      return (
        <div className="flex gap-[14px] items-center h-full">
          <Avatar
            size={SIZE.Small}
            user={{ fullName: value?.itemLabel, photoUrl: value?.photoUrl }}
          />
          {value?.itemLabel}
        </div>
      );
  }, [value]);

  return (
    <div className="w-full">
      {(label || information) && (
        <div className="flex items-center justify-between mb-[10px]">
          {label ? (
            <label htmlFor={name} className="text-sm font-semibold">
              {label}
              {required ? (
                <span className="text-danger-base">*</span>
              ) : (
                <span className="text-xs ml-[3px] leading-[18px] text-content">
                  {labelSubText}
                </span>
              )}
            </label>
          ) : null}
          {information ? (
            <div className="text-content">
              <Tooltip title={information} placement="top">
                <BsInfoCircle className="w-4 h-4" />
              </Tooltip>
            </div>
          ) : null}
        </div>
      )}
      <Dropdown
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        target={
          <div
            onClick={() => setIsOpen((prev) => !prev)}
            className={cn(
              inputClasses({ size: size as SIZE, textType: state }),
              'flex justify-between items-center',
              'min-w-[220px]',
              'py-0 flex-shrink-0 cursor-pointer',
            )}
          >
            <div
              role="button"
              className={cn(
                'max-w-[calc(100%-40px)] min-h-[36px]',
                'flex items-center',
              )}
            >
              <span
                className={cn(
                  renderValue ? 'text-secondary-base' : 'text-content',
                  'truncate max-w-full',
                )}
              >
                {renderValue ? renderValue : placeholder ?? ''}
              </span>
            </div>
            {!isEmpty(value) && (
              <VscClose
                role="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setValue(null);
                  onClear?.();
                }}
                className="w-5 h-5 transition hover:text-primary-base flex-shrink-0"
              />
            )}
          </div>
        }
      >
        <AsyncSelect
          defaultOptions
          {...props}
          autoFocus
          backspaceRemovesValue={false}
          classNames={{
            control({ isFocused }) {
              return cn(
                'border',
                cn(avatarSelectControl({ state, isFocused, size })),
              );
            },
          }}
          components={{
            Control: (props) =>
              Control({ ...props, onClose: () => setIsOpen(false) }),
            Option: (props) =>
              Option({ ...props, hideCheckbox, onClickAvatar }),
            DropdownIndicator: null,
            IndicatorSeparator: null,
          }}
          controlShouldRenderValue={false}
          hideSelectedOptions={true}
          cacheOptions
          menuIsOpen
          closeMenuOnSelect={!props.isMulti}
          onChange={(newValue, actionMeta) => {
            onChange?.(newValue, actionMeta);
            setValue(newValue as AvatarOption | AvatarOption[] | null);

            if (!props.isMulti) setIsOpen(false);
          }}
          {...(props.onInputChange && {
            onInputChange: debounce((val, actionMeta) => {
              props.onInputChange?.(val, actionMeta);
            }, 500),
          })}
          placeholder="Search..."
          styles={selectStyles}
          tabSelectsValue={false}
          noOptionsMessage={noOptionsMessage}
          value={value}
        />
      </Dropdown>
      {!!subText && (
        <div className={cn(subtext({ state, size }))}>{subText}</div>
      )}
    </div>
  );
};

export default SelectAvatar;
